import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, Calendar, Menu, X, Shield } from "lucide-react";
import { navLinks } from "../mock";
import { useSiteContent } from "../admin/SiteContentContext";

const BrandMark = ({ siteInfo }) => (
  <div className="flex items-center gap-3">
    <div className="w-12 h-12 shrink-0 rounded-xl bg-white/95 p-1 flex items-center justify-center">
      <img src={siteInfo.logo} alt={siteInfo.brand} className="w-full h-full object-contain" />
    </div>
    <div className="leading-tight">
      <div className="text-white font-semibold tracking-tight">{siteInfo.brand}</div>
      <div className="text-[11px] text-slate-400">{siteInfo.tagline}</div>
    </div>
  </div>
);

export default function Header() {
  const { content } = useSiteContent();
  const siteInfo = content.siteInfo;
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-[#0a1020]/85 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center"><BrandMark siteInfo={siteInfo} /></Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to}
              className={({ isActive }) => `nav-link text-[14px] text-slate-200/90 hover:text-white ${isActive ? "active text-white" : ""}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10">
            <div className="w-7 h-7 rounded-full overflow-hidden bg-slate-700">
              <img src={siteInfo.ceoImage} alt="CEO" className="w-full h-full object-cover"
                style={{ objectPosition: "50% 30%", transform: "scale(1.5)", transformOrigin: "center 25%" }} />
            </div>
            <span className="text-sm text-slate-200">{siteInfo.ceo}</span>
          </div>

          <Link to="/booking" className="btn-lime text-sm hidden sm:inline-flex">
            <Calendar className="w-4 h-4" /> Book Now
          </Link>

          <Link to="/admin" className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#c7e34a]/40 text-[#c7e34a] hover:bg-[#c7e34a]/10 transition text-sm font-semibold">
            <Shield className="w-4 h-4" /> Admin
          </Link>

          <button className="lg:hidden btn-ghost !p-2.5" onClick={() => setOpen((v) => !v)} aria-label="menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0a1020]">
          <div className="px-5 py-4 flex flex-col gap-2">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to}
                className={({ isActive }) => `py-3 px-3 rounded-lg text-slate-200 ${isActive ? "bg-white/5 text-white" : "hover:bg-white/5"}`}>
                {l.label}
              </NavLink>
            ))}
            <a href={`tel:${siteInfo.phoneRaw || siteInfo.phone}`} className="py-3 px-3 rounded-lg text-slate-200 flex items-center gap-2">
              <Phone className="w-4 h-4" /> {siteInfo.phone}
            </a>
            {siteInfo.email && (
              <a href={`mailto:${siteInfo.email}`} className="py-3 px-3 rounded-lg text-slate-200 flex items-center gap-2 break-all">
                <Mail className="w-4 h-4 shrink-0" /> {siteInfo.email}
              </a>
            )}
            <Link to="/booking" className="btn-lime mt-2 justify-center"><Calendar className="w-4 h-4" /> Book Now</Link>
            <Link to="/admin" className="btn-ghost mt-1 justify-center !border-[#c7e34a]/40 !text-[#c7e34a]"><Shield className="w-4 h-4" /> Admin</Link>
          </div>
        </div>
      )}

      {/* Sub-bar quote + contact */}
      <div className="border-t border-white/5 bg-[#0a1020]/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <p className="text-[13px] md:text-sm text-slate-300/90 italic">
            &ldquo;{siteInfo.ceoQuote}&rdquo;
          </p>
          <div className="flex items-center gap-4 shrink-0 flex-wrap">
            <span className="badge-pill"><Shield className="w-3 h-3" /> Contact {siteInfo.ceo}</span>
            <a href={`tel:${siteInfo.phoneRaw || siteInfo.phone}`} className="text-sm text-slate-300 hover:text-white flex items-center gap-1">
              <Phone className="w-3.5 h-3.5" /> {siteInfo.phone}
            </a>
            {siteInfo.email && (
              <a href={`mailto:${siteInfo.email}`} className="hidden md:inline-flex text-sm text-slate-300 hover:text-white items-center gap-1">
                <Mail className="w-3.5 h-3.5" /> {siteInfo.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
